import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { Button, Space, Tooltip, Table, Tag, Divider, Typography, Dropdown, Modal, message, Switch } from 'antd';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';
import { conferenceListApi, conferenceDelApi, conferenceMarkAsTesting, conferencetHiddenStateApi } from '@services/Conference';
import { getBossStaffs, getBossDepartments } from '@services/Enterprise';
import { numberProject, GetQueryString, flattenAllDepartments } from '@utils/utils';
import ConferenceFilterPanel from './ConferenceFilterPanel';
import { conferenceActions } from '../store/conferenceSlice';
import config from '@config';
import { conferenceTypes } from '../constants';
import PageHeader from '@components/PageHeader/PageHeader';
import { buildConferenceListMoreMenuItems, readConferenceWorkspacePermissions } from '@/layouts/data/conferenceMenuItems';
import './ConferenceList.less';
const { Text } = Typography;

const { setProjectFilter: setFilter, setProjectCursor: setCursor } = conferenceActions;

/** 工具栏快速筛选：2026 医院管理大会（正式会议） */
const QUICK_FILTER_HOSPITAL_MGMT_2026 = {
    conferenceId: 1497,
    displayName: '2026医院管理大会',
};

export default function ConferenceList() {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.conference.project.filter);
    const cursor = useSelector(state => state.conference.project.cursor);

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    // 项目负责人/负责部门 id -> 名称映射（前端反查）
    const [staffNameMap, setStaffNameMap] = useState({});
    const [departmentNameMap, setDepartmentNameMap] = useState({});
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const workspacePermissions = useMemo(() => readConferenceWorkspacePermissions(), []);
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const onFinish = values => {
        console.log(values);
        const newFilter = { ...filter };

        // 处理除日期外的其他字段
        Object.keys(values).forEach(key => {
            if (key !== 'dates') {
                newFilter[key] = values[key];
            }
        });

        // 单独处理日期字段，确保是有效的 dayjs 对象
        if (values.dates && values.dates.length === 2) {
            newFilter.dates = values.dates;
        } else {
            delete newFilter.dates;
        }

        dispatch(
            setFilter({
                ...newFilter,
                page: 1,
            }),
        );
    };

    const onReset = () => {
        dispatch(
            setFilter({
                ...filter,
                name: undefined,
                idList: undefined,
                dates: undefined,
                h3yunNo: undefined,
                createdBy: undefined,
                labelPayment: undefined,
                isTesting: undefined,
                page: 1,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onShowSizeChange = (_, pageSize) => onChangePage(1, pageSize);

    /**
     * 健康界平台展示开关（会议聚合列表、健康界平台合并后的统一开关）
     * 选择展示时，由接口侧校验该会议项目官网模块首页必填项是否均已配置（保持原会议聚合列表的限制逻辑），未配置时接口返回错误并提示
     */
    const onHealthPlatformChange = async (item, checked) => {
        const newValue = checked ? 0 : 1;
        setLoading(true);
        const params = {
            conferenceId: item.id,
            hidden: newValue,
            healthcarePlatformHidden: newValue,
        };
        const res = await conferencetHiddenStateApi(params);
        if (res.status_code === 200) {
            const newDataSource = tableData.map(data => {
                if (data === item) {
                    return { ...data, hidden: newValue, healthcarePlatformHidden: newValue };
                }
                return data;
            });
            setTableData(newDataSource);
        }
        setLoading(false);
    };

    const handleRefresh = () => getListData();

    const hospitalMgmt2026ProjectNo = useMemo(() => numberProject('YHY', QUICK_FILTER_HOSPITAL_MGMT_2026.conferenceId), []);

    const isHospitalMgmt2026QuickFilterActive = useMemo(() => {
        const idListStr = filter.idList?.replace(/YHY0+/gi, '') ?? '';
        const idArray = idListStr.split(',').filter(Boolean);
        return idArray.length === 1 && idArray[0] === String(QUICK_FILTER_HOSPITAL_MGMT_2026.conferenceId) && filter.isTesting === 0;
    }, [filter.idList, filter.isTesting]);

    const applyHospitalMgmt2026QuickFilter = () => {
        dispatch(
            setFilter({
                ...filter,
                name: undefined,
                idList: hospitalMgmt2026ProjectNo,
                dates: undefined,
                h3yunNo: undefined,
                createdBy: undefined,
                labelPayment: undefined,
                isTesting: 0,
                page: 1,
            }),
        );
    };

    const delConference = (id, name) => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
            content: `确认是否删除${name}`,
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                conferenceDelApi({ id: id }).then(res => {
                    if (res.status_code === 200) {
                        message.success('删除成功');
                        getListData();
                    }
                });
            },
        });
    };

    const gotoConferenceManagement = (projectId, moduleName, state = {}) => {
        dispatch(setCursor(projectId));

        navigate(`/conf/conferences/${projectId}${moduleName !== 'edit' ? `/${moduleName}` : ''}`, { state });
    };

    const markAsTesting = record => {
        Modal.confirm({
            title: '标记为测试会议',
            icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
            content: '医疗决策人数据统计等数据指标将排除标记为测试的会议项目，确定标记为测试项目？',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                conferenceMarkAsTesting({
                    id: record.id,
                    isTesting: !!record.isTesting ? 0 : 1,
                }).then(res => {
                    if (res.status_code === 200) {
                        const msg = !!record.isTesting ? '标记为正式会议成功' : '标记为测试会议成功';
                        message.success(msg);

                        getListData();
                    }
                });
            },
        });
    };

    const columns = [
        {
            title: '项目名称',
            dataIndex: 'name',
            width: 300,
            render: (_, record) => {
                return (
                    <>
                        <a target="_blank" href={`${config.HUIYI_DOMAIN}/web/${record.slug}`} style={{ verticalAlign: 'middle' }}>
                            {record.name}
                        </a>
                        {!!record.type && (
                            <Tag color="volcano" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                {conferenceTypes.find(item => item.type === record.type).label}
                            </Tag>
                        )}
                        {record.labelPayment === 0 && (
                            <Tag color="red" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                无需劳务
                            </Tag>
                        )}
                        {!!record.isTesting && (
                            <Tag color="#f50" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                TEST
                            </Tag>
                        )}
                        <div>氚云项目编号：{record.isBusiness != null && record.isBusiness !== '' && Number(record.isBusiness) === 0 ? '非经营项目' : record.h3yunNo || <>&ndash;</>}</div>
                        <div>
                            <Text type="secondary">会议项目编号：{numberProject('YHY', record.id)}</Text>
                        </div>
                    </>
                );
            },
        },
        {
            title: '执行日期',
            dataIndex: 'dates',
            width: 200,
            render: (_, record) => record.dates.join(', '),
        },
        {
            title: '项目相关人',
            dataIndex: 'createdBy',
            width: 220,
            render: (_, record) => {
                const managerNames = (Array.isArray(record.projectOwnerIds) ? record.projectOwnerIds : String(record.projectOwnerIds || '').split(',')).map(id => staffNameMap[String(id).trim()]).filter(Boolean);
                const departmentNames = (Array.isArray(record.projectDepartmentIds) ? record.projectDepartmentIds : String(record.projectDepartmentIds || '').split(',')).map(id => departmentNameMap[String(id).trim()]).filter(Boolean);
                return (
                    <>
                        <div>项目负责人：{managerNames.length ? managerNames.join('、') : <Text type="secondary">&ndash;</Text>}</div>
                        <div>负责部门：{departmentNames.length ? departmentNames.join('、') : <Text type="secondary">&ndash;</Text>}</div>
                        <div>创建人：{record.createdBy || <Text type="secondary">&ndash;</Text>}</div>
                    </>
                );
            },
        },
        {
            title: (
                <>
                    <span>健康界平台</span>
                    <Tooltip title={<>开启后，会议将在平台“会议聚合列表、APP会议直播”中展示。若该项目未配置官网首页，则无法开启。请先前往“官网模块”完成首页配置。</>}>
                        <i className="bi bi-question-circle-fill popover-tip-icon" aria-hidden="true" />
                    </Tooltip>
                </>
            ),
            dataIndex: 'healthcarePlatformHidden',
            width: 110,
            render: (_, record) => <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={!record.hidden && !record.healthcarePlatformHidden} onChange={checked => onHealthPlatformChange(record, checked)} />,
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 300,
            fixed: 'right',
            render: (_, record) => {
                const conferenceState = { name: record.name, slug: record.slug };
                const moreMenuItems = buildConferenceListMoreMenuItems(record, workspacePermissions, {
                    gotoConferenceManagement,
                    markAsTesting,
                    delConference,
                });

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'site-modules', conferenceState)}>
                            官网模块
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'agendas', conferenceState)}>
                            会议日程
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'guests', conferenceState)}>
                            会议嘉宾
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'signup', conferenceState)}>
                            报名签到
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'lives', conferenceState)}>
                            会议直播
                        </Button>
                        <Button size="small" type="link" onClick={() => gotoConferenceManagement(record.id, 'edit', conferenceState)}>
                            编辑会议项目
                        </Button>
                        {moreMenuItems.length > 0 && (
                            <Dropdown menu={{ items: moreMenuItems }} placement="bottomRight" trigger={['hover']}>
                                <Button type="link" size="small" className="more-operation" aria-label="更多操作">
                                    <i className="bi bi-three-dots" aria-hidden="true" />
                                </Button>
                            </Dropdown>
                        )}
                    </Space>
                );
            },
        },
    ];

    const getListData = async projectName => {
        const { idList, dates } = filter;

        const params = { ...filter };

        // 处理日期
        if (dates && Array.isArray(dates) && dates.length === 2) {
            try {
                const startDate = dayjs.isDayjs(dates[0]) ? dates[0] : dayjs(dates[0]);
                const endDate = dayjs.isDayjs(dates[1]) ? dates[1] : dayjs(dates[1]);

                if (startDate.isValid() && endDate.isValid()) {
                    params['dateBegin'] = startDate.format('YYYY-MM-DD');
                    params['dateEnd'] = endDate.format('YYYY-MM-DD');
                }
            } catch (e) {
                console.error('日期格式无效:', e);
                delete params.dateBegin;
                delete params.dateEnd;
            }
        }

        if (!!idList) {
            const idListStr = idList?.replace(/YHY0+/gi, '') || '';
            const idArray = idListStr.split(',') || [];
            if (idArray.length) {
                params['ids'] = idArray.map(id => parseInt(id) || 0) || [];
            }
        }

        if (isEmpty(params['ids'])) {
            params['ids'] = [];
        }

        if (projectName) {
            params.name = projectName;
        }

        try {
            setLoading(true);

            const res = await conferenceListApi(params);
            if (res.status_code === 200) {
                setTotal(res.data.totalCount);

                const list = res.data.list.map(item => {
                    return {
                        ...item,
                        key: item.id,
                        dates: item.dates ? item.dates?.split(',') : [],
                    };
                });

                setTableData(list);
            }

            setLoading(false);
        } catch {
            setLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        getListData();
    }, [filter]);

    // 医项目跳转携带参数
    // biome-ignore lint/correctness/useExhaustiveDependencies: 仅在挂载时执行
    useEffect(() => {
        const projectName = GetQueryString('projectName');
        if (projectName) {
            dispatch(setFilter({ ...filter, name: unescape(projectName), page: 1 }));
        }
    }, []);

    // 加载项目负责人/负责部门名称映射（列表反查展示用，仅挂载时请求一次）
    useEffect(() => {
        getBossStaffs().then(res => {
            if (res?.status_code === 200) {
                const map = {};
                (res.data || []).forEach(staff => {
                    map[staff.id] = staff.name;
                });
                setStaffNameMap(map);
            }
        });
        getBossDepartments().then(res => {
            if (res?.status_code === 200) {
                const map = {};
                flattenAllDepartments(res.data).forEach(dept => {
                    map[dept.id] = dept.name;
                });
                setDepartmentNameMap(map);
            }
        });
    }, []);

    useEffect(() => {
        document.title = '会议项目管理-医会议';
    }, []);

    return (
        <div className="page-container list-page-container conference-list-page">
            <PageHeader title="会议项目" breadcrumb={[{ label: '医会议', path: '/conf/conferences' }, { label: '会议项目' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <ConferenceFilterPanel filter={filter} onFinish={onFinish} onReset={onReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container conference-list-table-region">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={() => navigate(`/conf/conferences/new`)}>
                                新增会议项目
                            </Button>
                            <Space size="small" className="conference-list-quick-filters">
                                <Tooltip title={`正式会议，会议项目编号 ${hospitalMgmt2026ProjectNo}`}>
                                    <Button type={isHospitalMgmt2026QuickFilterActive ? 'primary' : 'default'} ghost={isHospitalMgmt2026QuickFilterActive} onClick={applyHospitalMgmt2026QuickFilter}>
                                        {QUICK_FILTER_HOSPITAL_MGMT_2026.displayName}
                                    </Button>
                                </Tooltip>
                            </Space>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap conference-list conference-list-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        bordered
                        sticky
                        size="medium"
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
